import {
    Avatar,
    Box,
    CircularProgress,
    Container,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Card, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { UrlConstant } from "../../../commom/UrlConstant";
import Axios from "../../../config/axios";
import RightSideSection from "../SharedComponent/RightSideSection";
import { CommonDataTable } from "../SharedComponent/CommonDataTable";
import { usePriceFormatter } from "../../../hooks";
import { formatPercentage } from "../../../utils/formatting";

function renderChange(value?: number, formatter?: any) {
    if (typeof value !== "number") return <span>N/A</span>;

    let color = "#B0B3B8";

    if (value > 0) {
        color = "#4edf6d";
    } else if (value < 0) {
        color = "red";
    }

    return (
        <span style={{ color }} title={formatter.display(value, {
            showSign: true,
        })}>
            {formatter.display(value, {
                showSign: true,
            })}
        </span>
    );
}

function renderPercentChange(value?: number) {
    if (typeof value !== "number") return <span>N/A</span>;

    let color = "#B0B3B8";

    if (value > 0) {
        color = "#4edf6d";
    } else if (value < 0) {
        color = "red";
    }

    return (
        <span
            style={{ color }}
            title={formatPercentage(value, {
                showSign: true,
            })}
        >
            {formatPercentage(value, {
                showSign: true,
            })}
        </span>
    );
}

function StockMarket() {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [stocks, setStocks] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const formatter = usePriceFormatter();

    const columns = [
        {
            name: "Name",
            selector: (row: any) => row.symbol,
            cell: (row: any) => (
                <div
                    className="stock-label"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                    }}
                >
                    <Avatar
                        src={row.branding?.icon_url ?? row.branding?.logo_url}
                        alt={row.symbol}
                        sx={{
                            width: 30,
                            height: 30,
                            backgroundColor: "white",
                            objectFit: "fill",
                        }}
                    >
                        {row.symbol?.[0]}
                    </Avatar>
                    <span>{row.symbol}</span>
                </div>
            ),
            sortable: true,
            center: false,
            width: "200px",
        },
        {
            name: "Company",
            selector: (row: any) => row.displayName,
            cell: (row: any) => (
                <span
                    style={{
                        display: "inline-block",
                        width: "100%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {row.displayName}
                </span>
            ),
            sortable: true,
            center: false,
            width: "200px",
        },
        {
            name: "Price",
            selector: (row: any) => row.Last ?? row.stockPrice,
            cell: (row: any) => (
                <span>{formatter.display(row.Last ?? row.stockPrice)}</span>
            ),
            sortable: true,
            center: true,
            width: "150px",
        },
        {
            name: "Change",
            selector: (row: any) => row.Change,
            cell: (row: any) => renderChange(row.Change, formatter),
            sortable: true,
            center: true,
            width: "150px",
        },
        {
            name: "Change%",
            selector: (row: any) => row.ChangePercentage,
            cell: (row: any) => renderPercentChange(row.ChangePercentage),
            sortable: true,
            center: true,
            width: "120px",
        },
    ];

    const fetchStocks = async () => {
        setLoading(true);
        try {
            const response = await Axios.get(
                UrlConstant.TopStocks.GetTopStocksByMarketCap
            );
            const list = response.data?.data?.stocks || [];
            setStocks(list);
            setTotalRows(list.length);
        } catch (error) {
            setStocks([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStocks();
        // eslint-disable-next-line
    }, [page, rowsPerPage]);

    return (
        <Box className="bg-color" display="flex">
            <Container
                maxWidth={false}
                className="bg-color p-3 px-5"
                style={{
                    minHeight: "calc(100vh - 71px - 143px)",
                    width: "70%",
                }}
            >
                <Typography
                    className="text-secondary"
                    sx={{ marginBottom: "20px", paddingTop: "30px" }}
                >
                    <span
                        onClick={() => navigate("/dashboard")}
                        style={{ color: "#4A7AF2", cursor: "pointer" }}
                    >
                        Dashboard
                    </span>{" "}
                    / Top Stocks
                </Typography>
                <Box mb={5}>
                    <Box display="flex" alignItems="center" mb={1}>
                        <Typography
                            className="text-white"
                            mb={0}
                            style={{ fontSize: "1.5rem" }}
                        >
                            Top Stocks
                        </Typography>
                    </Box>
                </Box>
                <Container
                    style={{ width: "100%", left: "0", margin: "0" }}
                    className="p-0"
                >
                    <Row className="dark-tbl no-border p-0">
                        <Card className="custom-card overflow-hidden bg-color w-100">
                            <Card.Body className="ps-12 p-2">
                                <div>
                                    {loading ? (
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            height="200px"
                                        >
                                            <CircularProgress color="primary" />
                                        </Box>
                                    ) : (
                                        <CommonDataTable
                                            setPage={setPage}
                                            setRowsPerPage={setRowsPerPage}
                                            data={stocks}
                                            totalRows={totalRows}
                                            columns={columns}
                                            title={"Top Stocks"}
                                            rowNavigateTo={(row) => ({
                                                url: "/stockdetail",
                                                state: {
                                                    symbol: row.symbol,
                                                    icon:
                                                        row.branding?.icon_url ??
                                                        row.branding?.logo_url,
                                                    title: "Top Stocks",
                                                },
                                            })}
                                        />
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Row>
                </Container>
            </Container>
            <Container style={{ width: "40%", paddingTop: "20px" }}>
                <RightSideSection showToggle={false} />
            </Container>
        </Box>
    );
}

export default StockMarket;